/**
 * ShiftRoomsPage — RelayCore's main PTT workspace
 * Shows: site selector → channels for that site → shift info → PTT button
 * Foreman role gets an "Override" button that cuts through active transmissions.
 */
import React, { useEffect, useRef, useState } from "react";
import { Radio, MapPin, Clock, Volume2, Users, ChevronDown, AlertTriangle } from "lucide-react";
import { api, createWS } from "../api";
import { useAuth } from "../AuthContext";
import { usePTT } from "../hooks/usePTT";

function PTTButton({ transmitting, onStart, onStop, disabled }) {
  return (
    <div className="flex flex-col items-center gap-2 select-none">
      <button
        disabled={disabled}
        onMouseDown={() => onStart(false)}
        onMouseUp={onStop}
        onTouchStart={(e) => { e.preventDefault(); onStart(false); }}
        onTouchEnd={(e)   => { e.preventDefault(); onStop(); }}
        className={`w-24 h-24 rounded-full flex items-center justify-center text-white transition-all duration-150 active:scale-95
          ${disabled ? "bg-gray-700 cursor-not-allowed" :
            transmitting ? "bg-green-500 ptt-active shadow-lg shadow-green-500/40" :
            "bg-indigo-600 hover:bg-indigo-500 shadow-md"}`}
      >
        <Radio className="w-10 h-10" />
      </button>
      <p className="text-xs text-gray-400">{disabled ? "Select a channel" : transmitting ? "Transmitting…" : "Hold to Talk"}</p>
    </div>
  );
}

export default function ShiftRoomsPage() {
  const { user } = useAuth();
  const [sites, setSites]       = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [shifts, setShifts]     = useState([]);
  const [messages, setMessages] = useState([]);
  const [presence, setPresence] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [wsState, setWsState]   = useState("disconnected");
  const [loading, setLoading]   = useState(true);
  const wsRef = useRef(null);
  const msgEndRef = useRef(null);
  const token = user?.token || "";
  const isForeman = user?.role === "admin" || user?.role === "employer";
  const activeSiteId = activeSite?.site_id;

  // Load sites on mount
  useEffect(() => {
    api.getSites().then(setSites).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load channels + shifts when site changes
  useEffect(() => {
    if (!activeSiteId) { setChannels([]); setShifts([]); return; }
    Promise.all([
      api.getChannels(activeSiteId),
      api.getShifts(activeSiteId),
    ]).then(([chs, shs]) => { setChannels(chs); setShifts(shs); }).catch(() => {});
    setActiveChannel(null);
  }, [activeSiteId]);

  // WebSocket when channel changes
  useEffect(() => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (!activeChannel || !token) { setWsState("disconnected"); setPresence([]); return; }

    const ws = createWS(activeChannel.channel_id, token, {
      onOpen:  ()    => setWsState("connected"),
      onClose: ()    => setWsState("disconnected"),
      onEvent: (evt) => {
        handleEvent(evt);
        if (evt.type === "presence") setPresence(evt.online || []);
      },
      onAudio: (buf) => playAudio(buf),
    });
    wsRef.current = ws;
    api.getMessages(activeChannel.channel_id).then(setMessages).catch(() => {});
    return () => ws.close();
    // eslint-disable-next-line
  }, [activeChannel?.channel_id, token]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const { transmitting, speakingUser, startPTT, stopPTT, playAudio, handleEvent } = usePTT(wsRef.current, isForeman);

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !activeChannel) return;
    const text = chatInput.trim();
    setChatInput("");
    try {
      const msg = await api.sendMessage(activeChannel.channel_id, text);
      setMessages((m) => [...m, msg]);
    } catch {}
  };

  // Linked shift for this channel
  const linkedShift = shifts.find((s) => s.shift_id === activeChannel?.shift_id);

  return (
    <div className="flex flex-1 h-screen overflow-hidden">
      {/* Left: Site + Channel Panel */}
      <div className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col">
        {/* Site Selector */}
        <div className="p-4 border-b border-gray-800">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Site</p>
          {loading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : (
            <div className="relative">
              <select
                value={activeSite?.site_id || ""}
                onChange={(e) => setActiveSite(sites.find((s) => s.site_id === e.target.value) || null)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white appearance-none focus:outline-none focus:border-indigo-500"
              >
                <option value="">Select a site…</option>
                {sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-3 top-2.5 pointer-events-none" />
            </div>
          )}
        </div>

        {/* Channel List */}
        <div className="flex-1 overflow-y-auto p-2">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide px-2 mb-2">Channels</p>
          {channels.length === 0 ? (
            <p className="text-xs text-gray-600 px-2">{activeSite ? "No channels" : "Pick a site"}</p>
          ) : (
            channels.map((ch) => (
              <button
                key={ch.channel_id}
                onClick={() => setActiveChannel(ch)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeChannel?.channel_id === ch.channel_id
                    ? "bg-indigo-600/20 border border-indigo-500/40 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <Radio className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{ch.name}</span>
                </div>
                {ch.shift_id && (
                  <p className="text-xs text-gray-600 mt-0.5 pl-4 truncate">Shift linked</p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Active Shifts */}
        {shifts.length > 0 && (
          <div className="p-4 border-t border-gray-800">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Active Shifts</p>
            <div className="space-y-1.5">
              {shifts.filter((s) => s.status === "active").map((sh) => (
                <div key={sh.shift_id} className="bg-gray-800 rounded-lg px-2.5 py-1.5">
                  <p className="text-xs text-white font-medium truncate">{sh.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Clock className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-500">
                      {sh.start_time ? new Date(sh.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                    {!sh.foreman_id && (
                      <span className="flex items-center gap-0.5 text-xs text-yellow-500">
                        <AlertTriangle className="w-3 h-3" /> No foreman
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main: Chat + PTT */}
      <div className="flex-1 flex flex-col">
        {activeChannel ? (
          <>
            {/* Channel Header */}
            <div className="px-6 py-3 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h1 className="font-semibold text-white flex items-center gap-2">
                  <Radio className="w-4 h-4 text-indigo-400" />
                  {activeChannel.name}
                </h1>
                {linkedShift && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Shift: {linkedShift.name}
                    {linkedShift.foreman_id && ` · Foreman: ${linkedShift.foreman_id}`}
                  </p>
                )}
                {activeSite && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {activeSite.name}
                  </p>
                )}
              </div>
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                wsState === "connected" ? "bg-green-500/20 text-green-400" : "bg-gray-700 text-gray-400"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${wsState === "connected" ? "bg-green-400" : "bg-gray-500"}`} />
                {wsState === "connected" ? "Live" : "Offline"}
              </span>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Messages */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {messages.map((m) => (
                    <div key={m.msg_id} className="flex gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-600/40 flex items-center justify-center text-xs text-white font-bold flex-shrink-0">
                        {(m.sender_name || m.sender_id || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-semibold text-white">{m.sender_name || m.sender_id}</span>
                          <span className="text-xs text-gray-500">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <p className="text-sm text-gray-200">{m.text}</p>
                      </div>
                    </div>
                  ))}
                  {speakingUser && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <Volume2 className="w-4 h-4 animate-pulse" /> {speakingUser} is transmitting…
                    </div>
                  )}
                  <div ref={msgEndRef} />
                </div>
                <form onSubmit={sendChat} className="p-4 border-t border-gray-800 flex gap-2">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder={`Message #${activeChannel.name}`}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-500">Send</button>
                </form>
              </div>

              {/* PTT + Presence */}
              <div className="w-56 border-l border-gray-800 bg-gray-900 flex flex-col items-center gap-6 p-6">
                <PTTButton
                  transmitting={transmitting}
                  onStart={startPTT}
                  onStop={stopPTT}
                  disabled={wsState !== "connected"}
                />

                {/* Foreman Override Button */}
                {isForeman && (
                  <div className="flex flex-col items-center gap-1">
                    <button
                      disabled={wsState !== "connected"}
                      onMouseDown={() => startPTT(true)}
                      onMouseUp={stopPTT}
                      onTouchStart={(e) => { e.preventDefault(); startPTT(true); }}
                      onTouchEnd={(e)   => { e.preventDefault(); stopPTT(); }}
                      className="px-4 py-2 rounded-lg bg-red-600/20 border border-red-500/40 text-red-400 text-xs font-medium hover:bg-red-600/30 disabled:opacity-50"
                    >
                      Foreman Override
                    </button>
                    <p className="text-xs text-gray-600">Cuts all transmissions</p>
                  </div>
                )}

                <div className="w-full">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Online
                  </p>
                  {presence.length === 0 ? (
                    <p className="text-xs text-gray-600">Only you</p>
                  ) : (
                    <div className="space-y-1.5">
                      {presence.map((u) => (
                        <div key={u.user_id} className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                          <span className="text-xs text-gray-300 truncate">{u.name || u.user_id}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <Radio className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-400 font-medium">Select a site and channel to start</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
